import { useState, useMemo, useCallback } from 'react';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useParams, useHistory, useLocation } from 'react-router-dom';

import Icon from '../../../../controls/Icon';
import { useUIConfig } from '../Context';
import BUILD_TIME_CONFIG_TYPES from '../../../../../config/buildTimeConfigTypes';

const JSX_TO_DISPLAY_VALUE_FOR = ['TextLabel', 'Label', 'TextBox'];

const Accordion = ({ name, path, config, showValuesInTree, isRoot, isBlock, isLayoutItem, layoutItemName, body }) => {
    const [expanded, setExpanded] = useState(true);
    const { selectDrillDownLayoutItem, setPropEditorPath, getMergedTemplate, currentOverrides, propEditorPath, getPropOverridesForLayout } = useUIConfig();

    const [translate] = useTranslation();
    const history = useHistory();
    const { state } = useLocation();

    const { objectSubType } = useParams();

    const isSelected = useMemo(() => {
        return getMergedTemplate(path, currentOverrides).path.join('/') === propEditorPath.join('/');
    }, [getMergedTemplate, path, currentOverrides, propEditorPath]);

    const clickHeader = useCallback((event) => {
        if (isLayoutItem) {
            let type;
            if (isBlock) {
                type = BUILD_TIME_CONFIG_TYPES.BLOCKS;
            }
            else if (isRoot) {
                type = BUILD_TIME_CONFIG_TYPES.APPLETS;
            }
            else {
                type = BUILD_TIME_CONFIG_TYPES.COMPONENTS;
            }
            selectDrillDownLayoutItem({
                type,
                name: layoutItemName || name,
                templateOverrides: config
            });

            const { path: updatedPath } = getMergedTemplate(path, currentOverrides);
            setPropEditorPath(updatedPath);

            history.replace({
                search: window.location.search,
                state: {
                    path: updatedPath,
                    type,
                    layoutItemName: layoutItemName || name,
                    templateOverrides: config,
                    keepBcContext: state?.keepBcContext
                }
            });

            event.stopPropagation();
        }
        else {
            setExpanded((prev) => !prev);
        }
    }, [isLayoutItem, layoutItemName, currentOverrides, getMergedTemplate, state, isBlock, isRoot, name, path, config, selectDrillDownLayoutItem, setPropEditorPath, history]);

    const getTypeLabel = useCallback(() => {
        if (isBlock || (objectSubType === BUILD_TIME_CONFIG_TYPES.BLOCKS && isRoot)) {
            return translate('OTM_BLOCK');
        }
        else if (isRoot) {
            return translate('OTM_APPLET');
        }

        return translate('OTM_COMPONENT');
    }, [isBlock, objectSubType, isRoot, translate]);

    const expandableIcon = useMemo(() => {
        if (!isRoot && (body?.length > 0 || !isLayoutItem)) {
            return (
                <Button
                    variant=''
                    className={`d-inline-flex p-0 border-0 ${isSelected ? 'text-white' : ''}`}
                    size='sm'
                    onClick={(event) => {
                        event.stopPropagation();
                        setExpanded((prev) => !prev);
                    }}
                >
                    {expanded ? <Icon icon='ArrowDropDown' className='fs-6' /> : <Icon icon='ArrowRight' className='fs-6' />}
                </Button>
            );
        }
        if (!isRoot) {
            return (<Button
                variant=''
                className={`d-inline-flex p-0 border-0 ${isSelected ? 'text-white' : ''}`}
                size='sm'
            >
                <Icon icon='Stop' className='fs-6' />
            </Button>)
        }
        return null;
    }, [isRoot, body, isLayoutItem, expanded, isSelected]);

    const renderValueForHeader = useMemo(() => {
        let layoutPropOverrides = getPropOverridesForLayout(path, getTypeLabel(), name.split('.')[1]);

        if (['LayoutItem'].includes(layoutPropOverrides.path)) {
            layoutPropOverrides = layoutPropOverrides.config;
        }

        if (!JSX_TO_DISPLAY_VALUE_FOR.includes(layoutPropOverrides.path)) {
            return;
        }


        return layoutPropOverrides.value;
    }, [path, getPropOverridesForLayout, getTypeLabel, name]);

    const renderedHeader = useMemo(() => {
        if (isLayoutItem) {
            return (
                <>
                    <span className='fw-bold'> {name} </span>
                    <span className='fw-bold fst-italic ms-1'>[{getTypeLabel()}]</span>
                    <span className='ms-1'> {showValuesInTree ? renderValueForHeader : ''} </span>
                </>
            );
        }
        return (
            <span>{name}</span>
        );
    }, [name, isLayoutItem, renderValueForHeader, showValuesInTree, getTypeLabel]);

    const renderedBody = useMemo(() => {
        if (expanded && body) {
            return body.map((params, i) => <Accordion key={i} showValuesInTree={showValuesInTree} {...params} />);
        }

        return null;
    }, [expanded, showValuesInTree, body]);

    return (
        <>
            <div className={`border-bottom d-flex ${isSelected ? 'bg-primary text-white' : ''}`}>
                {expandableIcon}
                <div onClick={clickHeader} className={`clickable`}>
                    {renderedHeader}
                </div>
            </div>
            <div className='ms-3'>
                {renderedBody}
            </div>
        </>
    );
};

export default Accordion;